/**
 * chatSpeak.js — อ่านข้อความแชทออกเสียง (16 ต.ค. 69)
 * ผู้ใช้: "ใส่เครื่องอ่านข้อความที่น้อง(เอไอ)ตอบมาเป็นเสียงได้มั้ย ไทยก็ไทย สากลก็สากล กดปุ่มแล้วอ่านออกเสียง"
 *
 * ใช้เสียงที่มากับเบราว์เซอร์/เครื่อง (Web Speech API) — ไม่เรียก API ข้างนอก ไม่เสียเงิน
 * แยกข้อความเป็นท่อนไทย / ท่อนอังกฤษ แล้วอ่านแต่ละท่อนด้วยเสียงภาษานั้น
 * ⚠ เสียงไทยต้องมีในเครื่อง (Chrome บนคอมส่วนใหญ่มี "Google ไทย" · Windows ต้องมีภาษาไทยติดตั้ง) — ไม่มีก็ยังอ่านด้วยเสียงที่มี
 *
 * รอบ 2 (ผู้ใช้: "เสียงไทยออกปกติ แต่ห่วยมาก ไม่ smooth เลย")
 *   - เดิมสลับเสียงทุกคำอังกฤษ (3.3V / ESP32) → หยุด-เริ่มใหม่ถี่ ๆ สะดุด
 *     ตอนนี้: ข้อความที่มีไทย อ่านด้วยเสียงไทยทั้งก้อน · สลับเป็นเสียงอังกฤษเฉพาะประโยคอังกฤษจริง (isSentence)
 *   - ตัดเป็นประโยค (Chrome อ่านก้อนยาวเกิน ~15 วินาทีแล้วเงียบไปเอง)
 *   - เลือกเสียง Natural/Online (Edge: Premwadee/Niwat) ก่อน → Google → เสียงในเครื่อง
 */

import { toast } from "react-toastify";

export const canSpeak = () => typeof window !== "undefined" && "speechSynthesis" in window && "SpeechSynthesisUtterance" in window;

const THAI = /[฀-๿]/;
const LATIN = /[A-Za-z]/;

// ล้างของที่อ่านแล้วไม่มีความหมาย: บรรทัดค่าใช้จ่าย · อีโมจิ · เครื่องหมาย markdown · ลิงก์ในระบบ
export function cleanForSpeech(text) {
  return String(text || "")
    .split("\n")
    .filter((l) => !l.trim().startsWith("💰"))
    .join("\n")
    .replace(/[\p{Extended_Pictographic}️⃣]/gu, " ")
    .replace(/\/(production|tasks|chat|projects)\/\S*/g, " ")
    .replace(/[*_#`>|•▶·—–]+/g, " ")
    .replace(/^\s*[-\d]+[.)]?\s+/gm, "")     // เลขข้อ / ขีดหน้าบรรทัด
    .replace(/\s*\n\s*/g, " . ")             // ขึ้นบรรทัดใหม่ = เว้นจังหวะ
    .replace(/(\s*\.\s*){2,}/g, " . ")
    .replace(/\s+/g, " ")
    .trim();
}

// แบ่งเป็นท่อน [{lang, text}] — ตัวเลข/เครื่องหมายติดไปกับท่อนก่อนหน้า
function splitFine(text) {
  const parts = [];
  for (const tok of String(text).split(/(\s+)/)) {
    if (!tok) continue;
    const lang = THAI.test(tok) ? "th" : LATIN.test(tok) ? "en" : null;
    const last = parts[parts.length - 1];
    if (!lang || (last && last.lang === lang)) {
      if (last) last.text += tok; else parts.push({ lang: "th", text: tok });
    } else {
      parts.push({ lang, text: tok });
    }
  }
  return parts.map((p) => ({ ...p, text: p.text.trim() })).filter((p) => p.text && /[\p{L}\p{N}]/u.test(p.text));
}

// ประโยคอังกฤษจริง = มีคำอังกฤษตัวเล็กธรรมดา ≥ 2 คำ และรวม ≥ 3 คำ (รหัส/ชื่อชิ้นส่วนอย่าง ESP32 LED D7 ไม่นับ)
const isSentence = (t) => {
  const w = t.split(/\s+/).filter((x) => /[A-Za-z]/.test(x));
  return w.length >= 3 && w.filter((x) => /^[a-z]{2,}[,.!?]?$/.test(x)).length >= 2;
};
export function splitByLanguage(text) {
  const fine = splitFine(text);
  if (!fine.some((p) => p.lang === "th")) return fine.length ? [{ lang: "en", text: fine.map((p) => p.text).join(" ") }] : [];
  // มีไทย: อังกฤษสั้น ๆ (รหัส ค่า ชื่อชิ้นส่วน) ให้เสียงไทยอ่านไปเลย ไม่สลับเสียง
  const out = [];
  for (const p of fine) {
    const lang = p.lang === "en" && !isSentence(p.text) ? "th" : p.lang;
    const last = out[out.length - 1];
    if (last && last.lang === lang) last.text += ` ${p.text}`;
    else out.push({ lang, text: p.text });
  }
  return out;
}

// ตัดเป็นประโยค/ก้อนไม่เกิน ~180 ตัวอักษร — ตัดที่ช่องว่างหรือเครื่องหมายจบประโยค
function chunks(text, max = 180) {
  const out = [];
  let rest = text.trim();
  while (rest.length > max) {
    const cut = Math.max(rest.lastIndexOf(" ", max), rest.lastIndexOf(".", max), rest.lastIndexOf("?", max));
    const at = cut > max * 0.4 ? cut + 1 : max;
    out.push(rest.slice(0, at).trim());
    rest = rest.slice(at).trim();
  }
  if (rest) out.push(rest);
  return out;
}

let voicesCache = [];
const loadVoices = () => {
  if (!canSpeak()) return [];
  const v = window.speechSynthesis.getVoices();
  if (v.length) voicesCache = v;
  return voicesCache;
};
if (canSpeak()) {
  loadVoices();
  window.speechSynthesis.addEventListener?.("voiceschanged", loadVoices);
}
const pickVoice = (lang) => {
  const all = loadVoices();
  const want = lang === "th" ? ["th-TH", "th"] : ["en-US", "en-GB", "en"];
  for (const w of want) {
    const list = all.filter((v) => v.lang?.toLowerCase().startsWith(w.toLowerCase()));
    // Natural/Online (Edge) ลื่นสุด → Google (Chrome) → เสียงในเครื่อง
    const best = list.find((v) => /natural|online/i.test(v.name)) || list.find((v) => /google/i.test(v.name)) || list[0];
    if (best) return best;
  }
  return null;
};
export const hasThaiVoice = () => Boolean(pickVoice("th") && pickVoice("th").lang?.toLowerCase().startsWith("th"));

// ── เสียง Azure ผ่านเซิร์ฟเวอร์ (16 ต.ค. 69 ผู้ใช้: "ใน Edge เสียงดีขึ้น แต่ส่วนมากใช้ Chrome") ──
//   /api/tts คืน mp3 เสียง Natural แบบเดียวกับ Edge · ยังไม่ตั้งค่า/ใช้ไม่ได้ = ใช้เสียงเบราว์เซอร์ข้างล่างแทน
let serverTts = null; // Promise<boolean>
const serverEnabled = () => {
  if (!serverTts) {
    serverTts = fetch("/api/tts/status", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { enabled: false }))
      .then((j) => Boolean(j.enabled))
      .catch(() => false);
  }
  return serverTts;
};
let audio = null;
// บอกคนฟังว่าทำไมเสียงเปลี่ยน/ไม่มีเสียง — กล่องแจ้งมุมจอ ครั้งเดียวต่อเหตุผลต่อการเปิดหน้า
const told = new Set();
const notice = (key, message) => {
  if (told.has(key)) return;
  told.add(key);
  toast.warning(message, { autoClose: 9000 });
};
const audioCache = new Map(); // id ข้อความ → blob url (กดซ้ำไม่ต้องโหลดใหม่)

// กำลังอ่านข้อความไหน — ปุ่มทุกปุ่มฟังเหตุการณ์นี้เพื่อเปลี่ยน "ฟัง" ↔ "หยุด"
let speakingId = null;
const emit = () => window.dispatchEvent(new CustomEvent("chat-speak", { detail: speakingId }));
export const currentSpeaking = () => speakingId;
const done = (id) => { if (speakingId === id) { speakingId = null; emit(); } };

export function stopSpeaking() {
  if (audio) { audio.pause(); audio = null; }
  if (canSpeak()) window.speechSynthesis.cancel();
  speakingId = null;
  emit();
}

function speakWithBrowser(id, parts) {
  if (!canSpeak()) {
    notice("none", "เครื่องนี้อ่านออกเสียงไม่ได้ — ลองเปิดด้วย Chrome หรือ Edge รุ่นล่าสุด");
    done(id);
    return;
  }
  if (parts.some((p) => p.lang === "th") && !hasThaiVoice()) {
    notice("no-th", "เครื่องนี้ไม่มีเสียงภาษาไทย — ติดตั้งภาษาไทยใน Windows (Settings → Time & Language → Speech) หรือเปิดด้วย Chrome/Edge");
  }
  const pieces = parts.flatMap((p) => chunks(p.text).map((t) => ({ lang: p.lang, text: t })));
  pieces.forEach((p, i) => {
    const u = new SpeechSynthesisUtterance(p.text);
    u.lang = p.lang === "th" ? "th-TH" : "en-US";
    const v = pickVoice(p.lang);
    if (v) u.voice = v;
    u.rate = 1;
    if (i === pieces.length - 1) u.onend = () => done(id);
    u.onerror = () => done(id);
    window.speechSynthesis.speak(u);
  });
}

export async function speakMessage(id, text) {
  stopSpeaking();
  const parts = splitByLanguage(cleanForSpeech(text));
  if (!parts.length) return false;
  speakingId = id;
  emit();
  if (await serverEnabled()) {
    try {
      let url = audioCache.get(id);
      if (!url) {
        const r = await fetch("/api/tts", {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ parts }),
        });
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          notice(`server-${j.reason || r.status}`, j.message || "ขอเสียงคุณภาพสูงไม่สำเร็จ — ใช้เสียงของเบราว์เซอร์แทน");
          throw new Error(String(r.status));
        }
        url = URL.createObjectURL(await r.blob());
        audioCache.set(id, url);
      }
      if (speakingId !== id) return true; // กดหยุด/กดข้อความอื่นระหว่างโหลด
      audio = new Audio(url);
      audio.onended = () => done(id);
      audio.onerror = () => done(id);
      // ปิดเสียงเครื่องอยู่ก็เล่นได้แต่ไม่ได้ยิน — ไม่มีทางรู้จากเบราว์เซอร์ ปุ่มจะเป็น "หยุด" ระหว่างเล่นให้เห็นว่ากำลังอ่าน
      await audio.play();
      return true;
    } catch {
      if (speakingId !== id) return true;
      // ใช้เสียงเบราว์เซอร์แทน
    }
  }
  speakWithBrowser(id, parts);
  return true;
}
